import animation, { fadeIn, fadeInUp, multicolouredAnimatedText, slideInUp, slideInUpInitial, } from '@helpers/animation';
import brand from '@helpers/brand';
import { cssVar, setCssVar } from '@helpers/cssVar';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { headerOverlap, headerOverlapPadding } from '@helpers/utils';
import BreadcrumbStyles from '@stories/Components/Breadcrumb/Breadcrumb.styles';
import { ButtonStyles } from '@stories/Components/Buttons/GeneralButton/Button.styles';
import CarouselControlsStyles from '@stories/Components/CarouselControls/CarouselControls.styles';
import SiteWide from '@stories/Components/Global/SiteWide/SiteWide';
import SiteWideStyles from '@stories/Components/Global/SiteWide/SiteWide.styles';
import HeadingStyles from '@stories/Components/Global/Typography/Heading.styles';
import SectionWrapper from '@stories/Components/SectionWrapper/SectionWrapper';
import { m } from 'framer-motion';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
import RichTextStyles from '../RichText/RichText.styles';
const Container = styled(SectionWrapper) `
  ${headerOverlap};
  ${headerOverlapPadding};

  ${({ theme }) => theme === 'sounds-of-london' &&
    css `
      ${setCssVar('fgColor', brand.white)};
      ${setCssVar('bgColor', brand.black)};
      ${setCssVar('bgColorAlt', brand.grey.grey10)};
      ${setCssVar('bgColorAlt2', brand.grey.grey10)};
      ${setCssVar('accentColor', brand.orange)};
    `}

  color: ${cssVar('fgColor')};

  ${BreadcrumbStyles.Container} {
    margin: 0 0 8px;

    @media ${from(Device.TabletLarge)} {
      margin: 0 0 48px;
      opacity: 0;
    }
  }

  @media ${from(Device.TabletLarge)} {
    ${({ hasImage }) => hasImage
    ? css `
            padding-top: 0;

            ${Bottom} {
              padding: ${fluid(40, 72)} 0 ${fluid(32, 72)};
            }
          `
    : css `
            ${Bottom} {
              padding: ${fluid(72, 144)} 0 ${fluid(72, 144)};
            }
          `}

    ${({ isActive }) => isActive &&
    css `
        ${Image}, ${Player} {
          transform: scale(1);
          opacity: 1;
        }

        ${BreadcrumbStyles.Container} {
          ${fadeIn(isActive, 375, 0, animation.easeInOutCubic)};
        }

        ${Tag} {
          ${slideInUp(isActive, 375, 0, animation.easeInOutCubic)};
        }

        ${Logo} {
          ${fadeInUp(isActive, 25, 375, 100, animation.easeInOutCubic)};
        }

        ${Title} {
          ${slideInUp(isActive, 375, 100, animation.easeInOutCubic)};
        }

        ${Content}, ${BottomRight} {
          ${fadeIn(isActive, 375, 200, animation.easeInOutCubic)};
        }
      `}
  }
`;
const Inner = styled.div ``;
const MediaWrapper = styled.div `
  display: none;

  @media ${from(Device.TabletLarge)} {
    position: relative;
    z-index: 1;
    display: block;
    min-height: 576px;
    width: 100%;
    overflow: hidden;
    background-color: ${brand.black};
    margin-top: var(--headerHeight);
  }
`;
const Controls = styled.div `
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  height: 150px;
  z-index: 5;
  background: linear-gradient(to bottom, transparent, ${rgba(brand.black, 0.75)});

  ${SiteWideStyles.Container} {
    position: relative;
    display: flex;
    align-items: flex-end;
    justify-content: flex-start;
    height: 100%;
    padding-bottom: 30px;
  }

  ${CarouselControlsStyles.Container} {
    height: 40px;
  }

  @media ${until(Device.Tablet)} {
    ${CarouselControlsStyles.SlideButton} {
      display: none;
    }
  }

  @media ${from(Device.Tablet)} {
    ${SiteWideStyles.Container} {
      justify-content: flex-end;
    }
  }

  @media ${from(Device.TabletLarge)} {
    opacity: 0;

    ${({ isActive }) => isActive &&
    css `
        ${fadeIn(isActive, 375, 300, animation.easeInOutCubic)};
      `}
  }
`;
const Image = styled(m.img) `
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  height: 100%;
  object-fit: cover;
  object-position: center center;
  width: 100%;
  transform: scale(1.02);
  opacity: 0;
  transform-origin: center center;
  transition: transform 700ms ease, opacity 700ms ease;
`;
const Player = styled(m.div) `
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: 100%;
  height: 100%;
  transform: scale(1.02);
  opacity: 0;
  transform-origin: center center;
  transition: transform 700ms ease, opacity 700ms ease;

  ${({ isExternal }) => isExternal &&
    css `
      height: 0;
      padding-bottom: 56.25%; /* 16:9 */
    `};

  video,
  iframe {
    position: absolute;
    left: 0;
    top: 0;
    display: block;
    height: 100%;
    width: 100%;
    pointer-events: none;
  }

  video {
    max-height: 720px;
    object-fit: cover;
    object-position: center center;
  }

  iframe {
    @media ${until(Device.DesktopSmall)} {
      transform: scale(1.6);
    }
  }
`;
const Bottom = styled.div `
  display: block;
  background: ${cssVar('bgColorAlt2')};
  padding: ${fluid(40, 72)} 0 ${fluid(32, 72)};
`;
const BottomInner = styled(SiteWide) `
  display: grid;
  gap: 0 20px;
  grid-template-columns: repeat(6, 1fr);

  @media ${from(Device.TabletLarge)} {
    gap: 0 30px;
    grid-template-columns: repeat(12, 1fr);
  }
`;
const BottomLeft = styled.div `
  grid-column: 1 / span 6;
  margin: 0 0 24px;

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 7;
    margin: 0;
  }
`;
const BottomRight = styled.div `
  grid-column: 1 / span 6;

  @media ${from(Device.TabletLarge)} {
    grid-column: 8 / span 5;
    opacity: 0;
  }
`;
const Tag = styled.small `
  display: block;
  ${fonts.brown.regular};
  font-size: ${fluid(16, 18)};
  letter-spacing: normal;
  line-height: 24px;
  margin: 0 0 ${fluid(8, 16)};
  text-transform: uppercase;

  @media ${from(Device.TabletLarge)} {
    ${slideInUpInitial};
    letter-spacing: 0.05em;
  }
`;
const Logo = styled.img `
  display: block;
  margin: 0 0 24px;
  max-height: 60px;

  @media ${from(Device.MobileLarge)} {
    max-height: 78px;
  }

  @media ${from(Device.TabletLarge)} {
    opacity: 0;
    transform: translateY(25px);
    max-height: 106px;
  }

  ${({ invertColour }) => invertColour &&
    css `
      filter: brightness(0) invert(1);
    `}
`;
const Title = styled.h1 `
  display: block;
  ${HeadingStyles.H1Styles};
  margin: 0 0 24px;
  max-width: 740px;

  ${({ isSrOnly }) => isSrOnly &&
    css `
      ${srOnly};
    `}

  @media ${from(Device.TabletLarge)} {
    ${slideInUpInitial};
  }

  [data-theme='sounds-of-london'] & span {
    ${multicolouredAnimatedText}
  }
`;
const Content = styled.div `
  ${RichTextStyles.contentStyles}
  margin: 0;
  max-width: 695px;

  @media ${from(Device.TabletLarge)} {
    opacity: 0;
  }
`;
const CtaWrapper = styled.div `
  display: flex;
  flex-direction: column;
  margin: ${fluid(48, 64)} 0 0;

  ${ButtonStyles.Button} + ${ButtonStyles.Button} {
    margin-top: 16px;
  }

  ${ButtonStyles.Button}:last-child {
    ${ButtonStyles.Icon} {
      display: none;
    }
  }

  @media ${from(Device.TabletLarge)} {
    align-items: center;
    flex-direction: row;

    ${ButtonStyles.Button} + ${ButtonStyles.Button} {
      margin-top: 0;
      margin-left: 16px;
    }
  }
`;
export default {
    Bottom,
    BottomInner,
    BottomLeft,
    BottomRight,
    Container,
    Content,
    Controls,
    CtaWrapper,
    Image,
    Player,
    MediaWrapper,
    Inner,
    Tag,
    Logo,
    Title,
};
